import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Modal } from 'react-bootstrap-v5';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import { Formik, Form, Field, FieldArray } from 'formik';
import moment from 'moment';
import 'moment/locale/tr';
import { useAppSelector } from 'src/services/redux/app/store';
import * as Yup from 'yup';
import { useSalesOpportunityCreate, useSalesStaffList } from '../service';
import { fetchAllCustomersForSelectAsync } from '../../customers/service';
import { KTSVG } from 'src/_metronic/helpers';
import PotentialCustomerCreate from '../../customers/modals/potential-customer-create';
import BasicInfoSurveyModal from '../../customers/modals/basic-info-survey-modal';
import { useCreateReminder } from '../../../helpers/reminders.service';

type Props = {
  show: boolean;
  handleClose: Function;
  refetchList: Function;
  preselectedCustomer?: {
    id: number;
    name: string;
    type: 'potential' | 'erp';
  } | null;
};

const validationSchema = Yup.object().shape({
  // ERP müşterileri için customer_carikartkodu, potansiyel müşteriler için customer_id
  customer_name: Yup.string().required('Müşteri seçimi zorunludur'),
  items: Yup.array()
    .of(
      Yup.object().shape({
        brand: Yup.string().required('Marka zorunludur'),
      }),
    )
    .min(1, 'En az bir marka eklenmelidir'),
});

export default function RecordCreate({ show, handleClose, refetchList, preselectedCustomer }: Props) {
  const { user } = useAppSelector((state) => state.auth);
  const [selectedSalesStaff, setSelectedSalesStaff] = useState<any>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [defaultCustomerOptions, setDefaultCustomerOptions] = useState<any[]>([]);
  const [potentialCustomerModal, setPotentialCustomerModal] = useState(false);
  const [surveyModal, setSurveyModal] = useState(false);

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Moment.js'i Türkçe yap
  moment.locale('tr');

  // Hatırlatıcı için form değerlerini tutacak ref
  const pendingReminderRef = useRef<{
    add_reminder: boolean;
    reminder_date: string;
    customer_name: string;
    brand: string;
    notify_crm: boolean;
    notify_email: boolean;
  } | null>(null);

  // Hatırlatıcı mutation
  const { mutate: createReminder } = useCreateReminder(
    () => {
      toast.success('Hatırlatıcı oluşturuldu');
    },
    (e: any) => {
      toast.error(e?.response?.data?.message || 'Hatırlatıcı oluşturulamadı');
    },
  );

  const onSuccess = (response: any) => {
    const opportunityId = response?.data?.id;

    // Eğer hatırlatıcı eklenecekse
    if (pendingReminderRef.current?.add_reminder && opportunityId) {
      createReminder({
        entity_type: 'sales_opportunity',
        entity_id: opportunityId,
        reminder_date: pendingReminderRef.current.reminder_date,
        title: `${pendingReminderRef.current.customer_name || 'Müşteri'} - ${pendingReminderRef.current.brand || 'Satış Fırsatı'}`,
        notify_crm: pendingReminderRef.current.notify_crm,
        notify_email: pendingReminderRef.current.notify_email,
      });
    }

    pendingReminderRef.current = null;
    toast.success('Satış fırsatı başarıyla oluşturuldu');
    handleClose();
    refetchList();
  };

  const onError = (e: any) => {
    e?.response?.data?.message && toast.error(e?.response?.data?.message);
  };

  const { mutate, isLoading } = useSalesOpportunityCreate(onSuccess, onError);

  // React Query ile veri çekme
  const { data: salesStaffData } = useSalesStaffList(onError);

  // Satış personeli listesini formatla
  const salesStaffList = useMemo(() => {
    return (
      salesStaffData?.results?.map((e: any) => ({
        value: e.id,
        label: `${e.name} ${e.surname}`,
      })) || []
    );
  }, [salesStaffData]);

  // Mevcut kullanıcıyı otomatik seç
  useEffect(() => {
    if (show && salesStaffList.length > 0 && user?.id && !selectedSalesStaff) {
      const currentUserStaff = salesStaffList.find((s: any) => s.value === user?.id);
      if (currentUserStaff) {
        setSelectedSalesStaff(currentUserStaff);
      }
    }
  }, [show, salesStaffList, user?.id, selectedSalesStaff]);

  // Müşterileri yükle
  const loadCustomers = useCallback(
    async (search: string = ''): Promise<any[]> => {
      try {
        const data = await fetchAllCustomersForSelectAsync({
          sales_staff: user?.id,
          sales_staff_key: user?.sales_staff_key,
          session_id: user?.dia_session_id,
          search,
          user_id: user?.id,
        });

        const formattedList = (data?.all || []).map((c: any) => ({
          value: c.type === 'potential' ? `potential_${c.id}` : c.id,
          label: c.name,
          isPotential: c.type === 'potential',
          isErp: c.type === 'erp',
          type: c.type,
          originalId: c.id,
          carikartkodu: c.carikartkodu,
          has_survey: c.has_survey,
        }));

        return formattedList;
      } catch (error) {
        console.error('Müşteriler yüklenirken hata:', error);
        return [];
      }
    },
    [user?.id, user?.sales_staff_key, user?.dia_session_id],
  );

  // Debounced load options for AsyncSelect
  const loadCustomerOptions = (inputValue: string): Promise<any[]> => {
    return new Promise((resolve) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(async () => {
        const options = await loadCustomers(inputValue);
        resolve(options);
      }, 400);
    });
  };

  // Modal açıldığında default options yükle ve state'leri sıfırla
  useEffect(() => {
    if (show) {
      // Preselected customer varsa onu seç
      if (preselectedCustomer) {
        const formattedCustomer = {
          value: preselectedCustomer.type === 'potential' ? `potential_${preselectedCustomer.id}` : preselectedCustomer.id,
          label: preselectedCustomer.name,
          isPotential: preselectedCustomer.type === 'potential',
          isErp: preselectedCustomer.type === 'erp',
          type: preselectedCustomer.type,
          originalId: preselectedCustomer.id,
        };
        setSelectedCustomer(formattedCustomer);
      } else {
        setSelectedCustomer(null);
      }
      loadCustomers('').then((options) => {
        setDefaultCustomerOptions(options);
      });
    }
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [show, preselectedCustomer, loadCustomers]);

  // Custom format for option label with badge
  const formatCustomerOptionLabel = (option: any, { context }: any) => (
    <div className="d-flex align-items-center justify-content-between">
      <span>{option.label}</span>
      {context === 'menu' && (
        <div>
          {option.isPotential && <span className="badge badge-light-warning ms-2 fs-9">Potansiyel</span>}
          {option.isErp && <span className="badge badge-light-primary ms-2 fs-9">DIA</span>}
        </div>
      )}
      {context === 'value' && (
        <div>
          {option.isPotential && <span className="badge badge-light-warning ms-2 fs-9">Potansiyel</span>}
          {option.isErp && <span className="badge badge-light-primary ms-2 fs-9">DIA</span>}
        </div>
      )}
    </div>
  );

  // Select stilleri
  const customerSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: '#f5f8fa',
      borderColor: state.isFocused ? '#009ef7' : '#e4e6ef',
      boxShadow: state.isFocused ? '0 0 0 1px #009ef7' : 'none',
      '&:hover': {
        borderColor: '#009ef7',
      },
      minHeight: '42px',
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected ? '#009ef7' : state.isFocused ? '#f1faff' : 'white',
      color: state.isSelected ? 'white' : '#181c32',
      cursor: 'pointer',
      padding: '10px 12px',
    }),
    menu: (base: any) => ({
      ...base,
      zIndex: 99999,
      boxShadow: '0 0 50px 0 rgba(82,63,105,.15)',
      borderRadius: '0.475rem',
    }),
    menuList: (base: any) => ({
      ...base,
      maxHeight: '280px',
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
  };

  const handleSubmit = (values: any) => {
    // Items dizisini formatla
    const formattedItems = values.items.map((item: any) => {
      const quantity = parseFloat(item.quantity) || 1;
      return {
        brand: item.brand,
        product: item.product || null,
        quantity: quantity,
      };
    });

    // Hatırlatıcı bilgilerini ref'e kaydet
    pendingReminderRef.current = {
      add_reminder: values.add_reminder,
      reminder_date: values.reminder_date,
      customer_name: values.customer_name,
      brand: values.items?.[0]?.brand || '',
      notify_crm: values.notify_crm,
      notify_email: values.notify_email,
    };

    mutate({
      description: values.description,
      sales_staff: values.sales_staff || null,
      items: formattedItems,
      customer_id: values.customer_id || null,
      customer_type: values.customer_type || null,
      customer_name: values.customer_name || null,
      customer_carikartkodu: values.customer_carikartkodu || null,
    });
  };

  return (
    <Modal show={show} onHide={() => handleClose()} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Yeni Satış Fırsatı</Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={{
          items: [{ brand: '', product: '', quantity: '1' }],
          description: '',
          sales_staff: user?.id || '',
          notify_crm: false,
          notify_email: false,
          customer_id: '',
          customer_type: '',
          customer_name: '',
          customer_carikartkodu: '',
          // Hatırlatıcı alanları
          add_reminder: false,
          reminder_date: moment().add(15, 'days').format('YYYY-MM-DD'),
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, touched, setFieldValue, values }) => (
          <Form>
            <Modal.Body>
              {/* Müşteri Seçimi */}
              <div className="mb-5">
                <label className="form-label required fw-bold">Müşteri</label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions={defaultCustomerOptions}
                  loadOptions={loadCustomerOptions}
                  value={selectedCustomer}
                  onChange={(option: any) => {
                    setSelectedCustomer(option);
                    // ERP müşterileri için customer_id null, potansiyel müşteriler için integer ID
                    const customerId = option?.type === 'potential' ? option?.originalId : null;
                    setFieldValue('customer_id', customerId || '');
                    setFieldValue('customer_type', option?.type || '');
                    setFieldValue('customer_name', option?.label || '');
                    setFieldValue('customer_carikartkodu', option?.carikartkodu || '');
                  }}
                  placeholder="Müşteri ara veya seç..."
                  loadingMessage={() => 'Aranıyor...'}
                  noOptionsMessage={() => (
                    <div className="text-center py-2 d-flex flex-column align-items-center">
                      <div className="text-muted mb-2">Müşteri bulunamadı</div>
                      <button
                        type="button"
                        className="btn btn-sm btn-light-primary d-flex flex-row align-items-center"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setPotentialCustomerModal(true);
                        }}
                      >
                        <KTSVG path="/media/icons/duotone/Navigation/Plus.svg" className="svg-icon svg-icon-5 me-1" />
                        Potansiyel Müşteri Ekle
                      </button>
                    </div>
                  )}
                  formatOptionLabel={formatCustomerOptionLabel}
                  styles={{
                    ...customerSelectStyles,
                    control: (base: any, state: any) => ({
                      ...customerSelectStyles.control(base, state),
                      borderColor: errors.customer_name && touched.customer_name ? '#f1416c' : state.isFocused ? '#009ef7' : '#e4e6ef',
                    }),
                  }}
                />
                {errors.customer_name && touched.customer_name && <div className="text-danger fs-7 mt-1">{errors.customer_name}</div>}
              </div>
              {selectedCustomer && (selectedCustomer.isPotential || selectedCustomer.isErp) && selectedCustomer.has_survey === false && (
                <div className="alert alert-warning d-flex align-items-center p-4 mb-5">
                  <i className="bi bi-exclamation-triangle-fill text-warning fs-3 me-3"></i>
                  <div className="d-flex flex-column flex-grow-1">
                    <span className="fw-bold">Bu müşterinin temel bilgileri henüz doldurulmamış.</span>
                    <span className="text-muted fs-7">Müşteri hakkında daha detaylı bilgi edinmek için temel bilgi anketini doldurabilirsiniz.</span>
                  </div>
                  <button type="button" className="btn btn-sm btn-warning ms-3" onClick={() => setSurveyModal(true)}>
                    <i className="bi bi-clipboard-check me-1"></i>
                    Temel Bilgileri Doldur
                  </button>
                </div>
              )}

              {/* Satış Personeli - Gizli */}
              <div className="d-none">
                <Select
                  options={salesStaffList}
                  value={selectedSalesStaff}
                  onChange={(option: any) => {
                    setSelectedSalesStaff(option);
                    setFieldValue('sales_staff', option?.value || '');
                  }}
                  placeholder="Satış personeli seçiniz..."
                  isClearable
                  isDisabled={user?.is_sales === true}
                />
              </div>

              {/* Marka/Ürün Listesi */}
              <FieldArray name="items">
                {({ push, remove }) => (
                  <div className="mb-5">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <label className="form-label fw-bold mb-0">Marka / Ürün Listesi</label>
                      <button
                        type="button"
                        className="btn btn-sm btn-light-primary d-flex flex-row align-items-center"
                        onClick={() => push({ brand: '', product: '', quantity: '1' })}
                      >
                        <KTSVG path="/media/icons/duotone/Navigation/Plus.svg" className="svg-icon svg-icon-5 me-1" />
                        Yeni Satır Ekle
                      </button>
                    </div>

                    {values.items.map((item: any, index: number) => (
                      <div key={index} className="card card-bordered border-dotted border-primary mb-3 position-relative">
                        <span
                          className="badge badge-light-primary  border-dotted border-primary"
                          style={{ position: 'absolute', top: '-8px', left: '-8px', zIndex: 1, border: '1px dotted' }}
                        >
                          #{index + 1}
                        </span>
                        <div className="card-body py-3 px-4">
                          <div className="d-flex align-items-center justify-content-end mb-2">
                            {values.items.length > 1 && (
                              <button type="button" className="btn btn-sm btn-light-danger m-0" onClick={() => remove(index)}>
                                <KTSVG path="/media/icons/duotone/General/Trash.svg" className="svg-icon svg-icon-3 d-flex flex-row m-0 " />
                              </button>
                            )}
                          </div>
                          <div className="row">
                            {/* Adet */}
                            <div className="col-2 col-md-1 mb-3">
                              <label className="form-label fw-bold fs-7">Adet</label>
                              <Field name={`items.${index}.quantity`} type="number" min="1" className="form-control form-control-sm" placeholder="1" />
                            </div>

                            {/* Marka */}
                            <div className="col-5 col-md-3 mb-3">
                              <label className="form-label required fw-bold fs-7">Marka</label>
                              <Field
                                name={`items.${index}.brand`}
                                className={`form-control form-control-sm ${(errors.items as any)?.[index]?.brand && (touched.items as any)?.[index]?.brand ? 'is-invalid' : ''}`}
                                placeholder="Marka adı..."
                              />
                              {(errors.items as any)?.[index]?.brand && (touched.items as any)?.[index]?.brand && (
                                <div className="text-danger fs-8 mt-1">{(errors.items as any)[index].brand}</div>
                              )}
                            </div>

                            {/* Ürün */}
                            <div className="col-5 col-md-8 mb-3">
                              <label className="form-label fw-bold fs-7">Ürün</label>
                              <Field name={`items.${index}.product`} className="form-control form-control-sm" placeholder="Ürün adı..." />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </FieldArray>

              {/* Açıklama */}
              <div className="mb-5">
                <label className="form-label fw-bold">Açıklama</label>
                <Field as="textarea" name="description" rows={4} className="form-control" placeholder="Açıklama..." />
              </div>

              {/* Hatırlatıcı ve Bildirim Seçenekleri */}
              <div className="mb-5">
                <div className="p-3 rounded bg-light-primary border border-primary border-dashed">
                  {/* Üst satır: Hatırlatıcı */}
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div className="form-check form-check-custom form-check-solid form-check-primary">
                        <Field type="checkbox" name="add_reminder" className="form-check-input bg-primary" id="add_reminder" />
                        <label className="form-check-label fw-semibold text-gray-700 ms-2 d-flex flex-row align-items-center" htmlFor="add_reminder">
                          <KTSVG path="/media/icons/duotone/Home/Clock.svg" className="svg-icon svg-icon-4 text-primary me-2" />
                          Hatırlatıcı Ekle
                        </label>
                      </div>
                    </div>

                    {values.add_reminder && (
                      <div className="d-flex align-items-center gap-2">
                        <div className="btn-group btn-group-sm border border-primary-300 border-dotted border-1 rounded" role="group">
                          <button
                            type="button"
                            className={`btn ${
                              values.reminder_date === moment().add(15, 'days').format('YYYY-MM-DD') ? 'btn-primary' : 'btn-light-primary'
                            } btn-sm border border-primary-300`}
                            onClick={() => setFieldValue('reminder_date', moment().add(15, 'days').format('YYYY-MM-DD'))}
                          >
                            15 Gün
                          </button>
                          <button
                            type="button"
                            className={`btn ${
                              values.reminder_date === moment().add(30, 'days').format('YYYY-MM-DD') ? 'btn-primary' : 'btn-light-primary'
                            } btn-sm border border-primary-300`}
                            onClick={() => setFieldValue('reminder_date', moment().add(30, 'days').format('YYYY-MM-DD'))}
                          >
                            30 Gün
                          </button>
                          <button
                            type="button"
                            className={`btn ${
                              values.reminder_date === moment().add(60, 'days').format('YYYY-MM-DD') ? 'btn-primary' : 'btn-light-primary'
                            } btn-sm border border-primary-300`}
                            onClick={() => setFieldValue('reminder_date', moment().add(60, 'days').format('YYYY-MM-DD'))}
                          >
                            60 Gün
                          </button>
                          <button
                            type="button"
                            className={`btn ${
                              values.reminder_date === moment().add(90, 'days').format('YYYY-MM-DD') ? 'btn-primary' : 'btn-light-primary'
                            } btn-sm border border-primary-300`}
                            onClick={() => setFieldValue('reminder_date', moment().add(90, 'days').format('YYYY-MM-DD'))}
                          >
                            90 Gün
                          </button>
                        </div>
                        <Field type="date" name="reminder_date" className="form-control form-control-sm w-auto" style={{ minWidth: '140px' }} />
                      </div>
                    )}
                  </div>

                  {/* Alt satır: Bildirimler - Sadece hatırlatıcı aktifken görünür */}
                  {values.add_reminder && (
                    <div className="d-flex align-items-center justify-content-end gap-4 mt-3 pt-3">
                      <div className="form-check form-check-custom form-check-solid form-check-primary">
                        <Field type="checkbox" name="notify_crm" className="form-check-input" id="notify_crm" />
                        <label className="form-check-label fw-semibold text-gray-700 ms-2 d-flex flex-row align-items-center" htmlFor="notify_crm">
                          <KTSVG path="/media/icons/duotone/General/Notifications1.svg" className="svg-icon svg-icon-4 text-primary me-1" />
                          CRM Bildirimi
                        </label>
                      </div>
                      <div className="form-check form-check-custom form-check-solid form-check-primary">
                        <Field type="checkbox" name="notify_email" className="form-check-input" id="notify_email" />
                        <label className="form-check-label fw-semibold text-gray-700 ms-2 d-flex flex-row align-items-center" htmlFor="notify_email">
                          <KTSVG path="/media/icons/duotone/Communication/Mail.svg" className="svg-icon svg-icon-4 text-primary me-1" />
                          Email Bildirimi
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button type="button" className="btn btn-light btn-sm" onClick={() => handleClose()} disabled={isLoading}>
                İptal
              </button>
              <button type="submit" className="btn btn-success btn-sm" disabled={isLoading}>
                {isLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                Kaydet
              </button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>

      {/* Potansiyel Müşteri Oluşturma Modal */}
      <PotentialCustomerCreate
        show={potentialCustomerModal}
        handleClose={() => setPotentialCustomerModal(false)}
        refetchList={() => {
          // Müşteri listesini yenile
          loadCustomers('').then((options) => {
            setDefaultCustomerOptions(options);
          });
        }}
      />

      {/* Temel Bilgi Anketi Modal */}
      {selectedCustomer && (selectedCustomer.isPotential || selectedCustomer.isErp) && (
        <BasicInfoSurveyModal
          show={surveyModal}
          handleClose={() => {
            setSurveyModal(false);
            // Anketi doldurduktan sonra müşteri listesini yenileyerek has_survey güncelle
            loadCustomers('').then((options) => {
              setDefaultCustomerOptions(options);
              const updatedCustomer = options.find((o: any) => o.value === selectedCustomer.value);
              if (updatedCustomer) setSelectedCustomer(updatedCustomer);
            });
          }}
          potentialCustomer={{ id: selectedCustomer.originalId, name: selectedCustomer.label }}
          customerType={selectedCustomer.isErp ? 'erp' : 'potential'}
        />
      )}
    </Modal>
  );
}
